/**
 * A tiny history-API router: no dependency, one server rule.
 *
 * Real paths (`/telc`, `/pass`), not `#/telc` — so every page is its own URL
 * that a crawler can index and a prerender can freeze. The one thing the server
 * owes us is to send unknown paths to `index.html`: `not_found_handling =
 * "single-page-application"` in `wrangler.toml` does that in production, and
 * Vite's dev server does it out of the box.
 *
 * Language lives in the path too: `/telc` is Polish, `/uk/telc` Ukrainian,
 * `/ru/telc` Russian, `/en/telc` English. Route matching never sees the prefix — `currentPath()`
 * and `useRoute()` strip it — so the route table stays monolingual. `navigate`
 * keeps whatever language the visitor is already in.
 *
 * Patterns look like `/exam/:attemptId`; a match returns the parameters.
 */
import { useEffect, useState } from "react";

export type Params = Record<string, string>;

declare global {
  interface Window {
    /** Google Tag Manager's queue; only present where the container loads. */
    dataLayer?: Record<string, unknown>[];
  }
}

/** Languages that carry a path prefix. Polish is the default and carries none. */
const PREFIXED = ["uk", "ru", "en"] as const;
export type UrlLocale = "pl" | "uk" | "ru" | "en";

/** `/uk/telc` → `{ locale: "uk", path: "/telc" }`; `/telc` → `{ "pl", "/telc" }`. */
export function splitLocale(fullPath: string): { locale: UrlLocale; path: string } {
  const seg = fullPath.split("/")[1];
  if (seg === "uk" || seg === "ru" || seg === "en") {
    const rest = fullPath.slice(seg.length + 1);
    return { locale: seg, path: rest === "" ? "/" : rest };
  }
  return { locale: "pl", path: fullPath === "" ? "/" : fullPath };
}

/** `("/telc", "uk")` → `/uk/telc`; `("/telc", "pl")` → `/telc`. */
export function localize(path: string, locale: string): string {
  const clean = path.startsWith("/") ? path : `/${path}`;
  if ((PREFIXED as readonly string[]).includes(locale)) {
    return clean === "/" ? `/${locale}` : `/${locale}${clean}`;
  }
  return clean;
}

/** The current route, without language prefix or query string. */
export function currentPath(): string {
  return splitLocale(window.location.pathname).path;
}

/** The language the current URL is in. */
export function currentLocale(): UrlLocale {
  return splitLocale(window.location.pathname).locale;
}

/**
 * A same-origin navigation. `path` is given without a language prefix; the
 * visitor's current language is kept. `replace` swaps the current history
 * entry instead of pushing a new one. Either way a `popstate` is fired so
 * `useRoute` — and anything else listening — updates, exactly as the back
 * button would.
 */
export function navigate(path: string, replace = false): void {
  go(localize(path, currentLocale()), replace);
}

/**
 * Navigate to an already-complete path, prefix and all. `navigate` is for
 * moving around within a language; this is for changing which language — the
 * one case where the target prefix is not the current one.
 */
export function goTo(fullPath: string, replace = false): void {
  go(fullPath, replace);
}

function go(fullPath: string, replace: boolean): void {
  if (replace) {
    window.history.replaceState(null, "", fullPath);
  } else {
    window.history.pushState(null, "", fullPath);
    // A fresh page starts at the top; only the back button restores a scroll
    // position, and that path goes through popstate, not here.
    window.scrollTo(0, 0);
  }
  window.dispatchEvent(new PopStateEvent("popstate"));
  // The page view is reported by `usePageViews` (src/lib/analytics.ts), which
  // also hears the back button — this function never did.
}

/** The full path, prefix and all, as a hook. */
export function useFullPath(): string {
  const [path, setPath] = useState(() => window.location.pathname || "/");
  useEffect(() => {
    const onChange = (): void => setPath(window.location.pathname || "/");
    window.addEventListener("popstate", onChange);
    return () => window.removeEventListener("popstate", onChange);
  }, []);
  return path;
}

/** The current route without the language prefix. */
export function useRoute(): string {
  return splitLocale(useFullPath()).path;
}

/** The language the current URL is in, as a hook. */
export function useUrlLocale(): UrlLocale {
  return splitLocale(useFullPath()).locale;
}

/** `match("/exam/:id", "/exam/abc")` returns `{ id: "abc" }`, otherwise `null`. */
export function match(pattern: string, path: string): Params | null {
  const p = pattern.split("/").filter(Boolean);
  const s = path.split("/").filter(Boolean);
  if (p.length !== s.length) return null;

  const params: Params = {};
  for (let i = 0; i < p.length; i++) {
    const seg = p[i]!;
    const value = s[i]!;
    if (seg.startsWith(":")) params[seg.slice(1)] = decodeURIComponent(value);
    else if (seg !== value) return null;
  }
  return params;
}

/** Warns before the tab is closed while an exam is running. */
export function useBeforeUnload(active: boolean, message: string): void {
  useEffect(() => {
    if (!active) return;
    const handler = (e: BeforeUnloadEvent): string => {
      e.preventDefault();
      e.returnValue = message;
      return message;
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [active, message]);
}
